import {
  Receipt,
  Coins,
  ChartLineUp,
  Hourglass,
} from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import type { InvoiceListTotals, InvoiceLabels, Locale } from "./types";
import { fmtMoney } from "./utils";

interface Props {
  totals: InvoiceListTotals;
  labels: InvoiceLabels;
  locale: Locale;
}

export function InvoiceSummaryCards({ totals, labels, locale }: Props) {
  const cards = [
    {
      key: "count",
      icon: Receipt,
      label: labels.summary.totalCount,
      value: String(totals.totalCount),
      mono: true,
      tone: "primary" as const,
    },
    {
      key: "net",
      icon: ChartLineUp,
      label: labels.summary.netMinor,
      value: fmtMoney(totals.netMinor, locale),
      mono: true,
      tone: "primary" as const,
    },
    {
      key: "paid",
      icon: Coins,
      label: labels.summary.paidMinor,
      value: fmtMoney(totals.paidMinor, locale),
      mono: true,
      tone: "success" as const,
    },
    {
      key: "balance",
      icon: Hourglass,
      label: labels.summary.balanceMinor,
      value: fmtMoney(totals.balanceMinor, locale),
      mono: true,
      tone: totals.balanceMinor > 0 ? ("warning" as const) : ("muted" as const),
    },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" role="group" aria-label={labels.summary.title}>
      {cards.map((c) => {
        const Icon = c.icon;
        const ringTone =
          c.tone === "success"
            ? "text-biz-success bg-biz-success-subtle"
            : c.tone === "warning"
              ? "text-biz-warning bg-biz-warning-subtle"
              : c.tone === "primary"
                ? "text-biz-primary bg-biz-primary-subtle"
                : "text-biz-foreground-muted bg-biz-muted";
        return (
          <div
            key={c.key}
            className="bg-biz-surface-elevated border border-biz-border rounded-[10px] p-4 biz-shadow-sm flex items-start gap-3"
          >
            <div className={cn("h-10 w-10 rounded-lg flex items-center justify-center shrink-0", ringTone)}>
              <Icon size={20} weight="regular" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-biz-foreground-muted truncate">{c.label}</p>
              <p
                className={cn(
                  "text-xl font-semibold mt-0.5 truncate",
                  c.mono && "font-mono-num",
                  c.tone === "warning" && totals.balanceMinor > 0 && "text-biz-warning",
                )}
                title={c.value}
              >
                {c.value}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
